import { matches, teams } from "../data/worldCup2026.js";
import { calculatePredictionPoints, outcomeDelta } from "../utils/scoring.js";
import { normalizeParticipantType } from "../utils/csv.js";
import { findUserRank, rankUsers } from "../utils/ranking.js";
import { normalizeRegistrationPayload } from "../utils/registration.js";

function isKnockoutStage(stage) {
  return stage && stage !== "group";
}

function normalizePenaltyScore(value) {
  const score = Number(value);
  if (!Number.isInteger(score) || score < 0 || score > 30) {
    throw new Error("Ingresa penales enteros entre 0 y 30.");
  }
  return score;
}

function buildResultMetadata(match, result, winnerTeam, tiebreaker) {
  if (!isKnockoutStage(match.stage)) {
    return { winnerTeam: "", tiebreaker: null };
  }

  if (result.home > result.away) {
    return { winnerTeam: match.homeTeam, tiebreaker: null };
  }

  if (result.away > result.home) {
    return { winnerTeam: match.awayTeam, tiebreaker: null };
  }

  if (![match.homeTeam, match.awayTeam].includes(winnerTeam)) {
    throw new Error("Selecciona quién avanzó por penales.");
  }

  const penaltyHome = normalizePenaltyScore(tiebreaker?.home);
  const penaltyAway = normalizePenaltyScore(tiebreaker?.away);
  if (penaltyHome === penaltyAway) {
    throw new Error("El marcador de penales no puede quedar empatado.");
  }
  if (winnerTeam === match.homeTeam && penaltyHome < penaltyAway) {
    throw new Error("El ganador por penales no coincide con el marcador de penales.");
  }
  if (winnerTeam === match.awayTeam && penaltyAway < penaltyHome) {
    throw new Error("El ganador por penales no coincide con el marcador de penales.");
  }

  return {
    winnerTeam,
    tiebreaker: {
      type: "penalties",
      home: penaltyHome,
      away: penaltyAway
    }
  };
}

const STORAGE_KEY = "quiniela-cm-demo-store-v5";
const SESSION_KEY = "quiniela-cm-demo-session";
const listeners = new Set();

function uidFromEmail(email) {
  return email.toLowerCase().replace(/[^a-z0-9]/g, "-");
}

function seedStore() {
  const now = new Date().toISOString();
  const adminUid = "admin-centro-medico";
  const userUid = "ana-morales";

  return {
    users: {
      [adminUid]: {
        uid: adminUid,
        email: "admin@centromedico.com.gt",
        name: "Admin Centro Médico",
        role: "admin",
        participantType: "colaborador",
        employeeCode: "ADMIN-CM",
        professionalLicense: "",
        password: "quiniela2026",
        totalPoints: 18,
        correctOutcomes: 4,
        exactScores: 2,
        predictionsCount: 6,
        createdAt: now
      },
      [userUid]: {
        uid: userUid,
        email: "ana.morales@centromedico.com.gt",
        name: "Ana Morales",
        role: "user",
        participantType: "medico",
        professionalLicense: "10203",
        employeeCode: "",
        password: "quiniela2026",
        totalPoints: 12,
        correctOutcomes: 3,
        exactScores: 1,
        predictionsCount: 5,
        createdAt: now
      },
      "carlos-ruiz": {
        uid: "carlos-ruiz",
        email: "carlos.ruiz@centromedico.com.gt",
        name: "Carlos Ruiz",
        role: "user",
        participantType: "colaborador",
        employeeCode: "CM-014",
        professionalLicense: "",
        password: "quiniela2026",
        totalPoints: 9,
        correctOutcomes: 3,
        exactScores: 0,
        predictionsCount: 4,
        createdAt: now
      },
      "maria-lopez": {
        uid: "maria-lopez",
        email: "maria.lopez@centromedico.com.gt",
        name: "María López",
        role: "user",
        participantType: "medico",
        professionalLicense: "20405",
        employeeCode: "",
        password: "quiniela2026",
        totalPoints: 21,
        correctOutcomes: 5,
        exactScores: 2,
        predictionsCount: 7,
        createdAt: now
      }
    },
    authorizedEmails: {
      "admin@centromedico.com.gt": {
        name: "Admin Centro Médico",
        email: "admin@centromedico.com.gt",
        participantType: "colaborador",
        employeeCode: "ADMIN-CM",
        professionalLicense: "",
        used: true,
        uid: adminUid,
        importedAt: now
      },
      "ana.morales@centromedico.com.gt": {
        name: "Ana Morales",
        email: "ana.morales@centromedico.com.gt",
        participantType: "medico",
        professionalLicense: "10203",
        employeeCode: "",
        used: true,
        uid: userUid,
        importedAt: now
      },
      "nuevo.colaborador@centromedico.com.gt": {
        name: "Nuevo Colaborador",
        email: "nuevo.colaborador@centromedico.com.gt",
        participantType: "colaborador",
        employeeCode: "CM-NEW",
        professionalLicense: "",
        used: false,
        importedAt: now
      }
    },
    matches,
    teams,
    predictions: {
      [`${userUid}_match-001`]: {
        uid: userUid,
        matchId: "match-001",
        homeScore: 2,
        awayScore: 1,
        pointsEarned: 0,
        outcomeType: "pending",
        createdAt: now,
        updatedAt: now
      },
      [`${userUid}_match-002`]: {
        uid: userUid,
        matchId: "match-002",
        homeScore: 1,
        awayScore: 1,
        pointsEarned: 0,
        outcomeType: "pending",
        createdAt: now,
        updatedAt: now
      }
    },
    chatMessages: {
      "demo-chat-001": {
        id: "demo-chat-001",
        uid: "maria-lopez",
        text: "Ya hice mis primeros pronósticos. El grupo B va a estar buenísimo.",
        createdAt: new Date(Date.now() - 38 * 60 * 1000).toISOString()
      },
      "demo-chat-002": {
        id: "demo-chat-002",
        uid: adminUid,
        text: "Recuerden guardar sus marcadores antes del cierre de cada partido.",
        createdAt: new Date(Date.now() - 22 * 60 * 1000).toISOString()
      },
      "demo-chat-003": {
        id: "demo-chat-003",
        uid: userUid,
        text: "Listo, ya estamos en modo mundialista.",
        createdAt: new Date(Date.now() - 8 * 60 * 1000).toISOString()
      }
    }
  };
}

function readStore() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : seedStore();
  } catch {
    return seedStore();
  }
}

function writeStore(store) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  listeners.forEach((listener) => listener());
}

function getSessionUser(store = readStore()) {
  const email = localStorage.getItem(SESSION_KEY);
  if (!email) return null;
  return Object.values(store.users).find((user) => user.email === email) || null;
}

function snapshotForUser(uid) {
  const store = readStore();
  const profile = store.users[uid] || null;
  const userPredictions = Object.values(store.predictions).filter((prediction) => prediction.uid === uid);
  const ranking = rankUsers(Object.values(store.users));
  const chatMessages = Object.values(store.chatMessages || {})
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .slice(-80);

  return {
    profile,
    currentUserRank: findUserRank(ranking, uid),
    teams: store.teams,
    matches: store.matches,
    predictions: userPredictions,
    ranking,
    chatMessages,
    authorizedEmails: Object.values(store.authorizedEmails).sort((a, b) => a.email.localeCompare(b.email))
  };
}

function notifyAuth(callback) {
  const user = getSessionUser();
  callback(user ? { uid: user.uid, email: user.email } : null);
}

export const demoStore = {
  mode: "demo",
  onAuthStateChanged(callback) {
    notifyAuth(callback);
    const listener = () => notifyAuth(callback);
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  loginUser(email, password) {
    const store = readStore();
    const user = Object.values(store.users).find((item) => item.email === email.toLowerCase());
    if (!user || user.password !== password) {
      throw new Error("Revisa tu correo y contraseña.");
    }
    localStorage.setItem(SESSION_KEY, user.email);
    listeners.forEach((listener) => listener());
    return Promise.resolve(user);
  },
  sendPasswordReset(email) {
    const store = readStore();
    const user = Object.values(store.users).find((item) => item.email === email.toLowerCase());
    if (!user) {
      throw new Error("No encontramos una cuenta con ese correo.");
    }
    return Promise.resolve();
  },
  registerUser(payload) {
    const userInput = normalizeRegistrationPayload(payload);
    const store = readStore();
    const existingUser = Object.values(store.users).find((user) => user.email === userInput.email);

    if (existingUser || store.authorizedEmails[userInput.email]) {
      throw new Error("Ya existe un usuario con ese correo.");
    }

    const now = new Date().toISOString();
    const uid = uidFromEmail(userInput.email);
    const profile = {
      uid,
      email: userInput.email,
      name: userInput.name,
      role: "user",
      participantType: userInput.participantType,
      professionalLicense: userInput.participantType === "medico" ? userInput.professionalLicense : "",
      employeeCode: userInput.participantType === "colaborador" ? userInput.employeeCode : "",
      password: userInput.password,
      totalPoints: 0,
      correctOutcomes: 0,
      exactScores: 0,
      predictionsCount: 0,
      createdAt: now
    };

    store.users[uid] = profile;
    store.authorizedEmails[userInput.email] = {
      name: profile.name,
      email: profile.email,
      participantType: profile.participantType,
      professionalLicense: profile.professionalLicense,
      employeeCode: profile.employeeCode,
      used: true,
      uid,
      importedAt: now,
      activatedAt: now
    };
    localStorage.setItem(SESSION_KEY, profile.email);
    writeStore(store);
    return Promise.resolve(profile);
  },
  logoutUser() {
    localStorage.removeItem(SESSION_KEY);
    listeners.forEach((listener) => listener());
    return Promise.resolve();
  },
  subscribeWorkspace(uid, callback) {
    const emit = () => callback(snapshotForUser(uid));
    emit();
    listeners.add(emit);
    return () => listeners.delete(emit);
  },
  async savePrediction({ uid, matchId, homeScore, awayScore }) {
    const store = readStore();
    const match = store.matches.find((item) => item.id === matchId);
    if (!match) throw new Error("No encontramos ese partido.");
    if (new Date(match.lockAt).getTime() <= Date.now()) {
      throw new Error("Este partido ya está cerrado para predicciones.");
    }

    const id = `${uid}_${matchId}`;
    const previous = store.predictions[id];
    const now = new Date().toISOString();
    store.predictions[id] = {
      uid,
      matchId,
      homeScore,
      awayScore,
      pointsEarned: previous?.pointsEarned || 0,
      outcomeType: previous?.outcomeType || "pending",
      createdAt: previous?.createdAt || now,
      updatedAt: now
    };

    if (!previous) {
      store.users[uid].predictionsCount += 1;
    }

    writeStore(store);
  },
  async importAuthorizedEmails(rows) {
    const store = readStore();
    const now = new Date().toISOString();
    let imported = 0;
    let duplicates = 0;

    rows.forEach((row) => {
      const existingUser = Object.values(store.users).find((user) => user.email === row.email);
      if (existingUser || store.authorizedEmails[row.email]) {
        duplicates += 1;
        return;
      }
      const uid = uidFromEmail(row.email);
      store.users[uid] = {
        uid,
        email: row.email,
        name: row.name,
        role: "user",
        participantType: normalizeParticipantType(row.participantType),
        professionalLicense: row.participantType === "medico" ? row.professionalLicense || "" : "",
        employeeCode: row.participantType !== "medico" ? row.employeeCode || "" : "",
        password: row.password,
        totalPoints: 0,
        correctOutcomes: 0,
        exactScores: 0,
        predictionsCount: 0,
        createdAt: now
      };
      store.authorizedEmails[row.email] = {
        name: row.name,
        email: row.email,
        participantType: normalizeParticipantType(row.participantType),
        professionalLicense: row.participantType === "medico" ? row.professionalLicense || "" : "",
        employeeCode: row.participantType !== "medico" ? row.employeeCode || "" : "",
        used: true,
        uid,
        importedAt: now
      };
      imported += 1;
    });

    writeStore(store);
    return { imported, duplicates };
  },
  async importUsers(rows) {
    return demoStore.importAuthorizedEmails(rows);
  },
  async sendChatMessage({ uid, text }) {
    const cleanText = text.trim();
    if (!cleanText) throw new Error("Escribe un mensaje antes de enviarlo.");
    if (cleanText.length > 280) throw new Error("El mensaje debe tener 280 caracteres o menos.");

    const store = readStore();
    if (!store.users[uid]) throw new Error("No encontramos tu usuario para enviar el mensaje.");

    const id = `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    store.chatMessages = store.chatMessages || {};
    store.chatMessages[id] = {
      id,
      uid,
      text: cleanText,
      createdAt: new Date().toISOString()
    };

    const messages = Object.values(store.chatMessages).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    messages.slice(0, Math.max(0, messages.length - 120)).forEach((message) => {
      delete store.chatMessages[message.id];
    });

    writeStore(store);
    return store.chatMessages[id];
  },
  async updateMatch(matchId, patch) {
    const store = readStore();
    store.matches = store.matches.map((match) => (match.id === matchId ? { ...match, ...patch } : match));
    writeStore(store);
  },
  async saveMatchResult({ matchId, home, away, winnerTeam, tiebreaker }, onProgress = () => {}) {
    const store = readStore();
    const match = store.matches.find((item) => item.id === matchId);
    if (!match) throw new Error("No encontramos ese partido.");

    const matchingPredictions = Object.values(store.predictions).filter((prediction) => prediction.matchId === matchId);
    onProgress({
      message: matchingPredictions.length
        ? `Recalculando ${matchingPredictions.length} pronósticos...`
        : "Guardando resultado..."
    });

    match.result = { home: Number(home), away: Number(away) };
    const metadata = buildResultMetadata(match, match.result, winnerTeam, tiebreaker);
    match.winnerTeam = metadata.winnerTeam;
    match.tiebreaker = metadata.tiebreaker;
    match.status = "finished";
    match.scoringStatus = "complete";
    match.scoredPredictions = matchingPredictions.length;

    matchingPredictions.forEach((prediction, index) => {
        const previousPoints = prediction.pointsEarned || 0;
        const previousType = prediction.outcomeType || "none";
        const next = calculatePredictionPoints(prediction, match.result, match.multiplier);
        const delta = outcomeDelta(previousType, next.outcomeType);
        const user = store.users[prediction.uid];

        prediction.pointsEarned = next.points;
        prediction.outcomeType = next.outcomeType;
        prediction.updatedAt = new Date().toISOString();

        if (user) {
          user.totalPoints = Number(((user.totalPoints || 0) + next.points - previousPoints).toFixed(2));
          user.correctOutcomes = Math.max(0, (user.correctOutcomes || 0) + delta.correctOutcomes);
          user.exactScores = Math.max(0, (user.exactScores || 0) + delta.exactScores);
        }
        onProgress({
          message: `Recalculando puntos ${index + 1}/${matchingPredictions.length}...`,
          completed: index + 1,
          total: matchingPredictions.length
        });
      });

    writeStore(store);
    onProgress({ message: "Resultado confirmado y puntos recalculados." });
  },
  resetDemoData() {
    writeStore(seedStore());
  }
};
